class ParticipantService {
  constructor(apiBaseUrl) {
    this.apiBaseUrl = apiBaseUrl;
  }

  async fetchParticipants() {
    const [clubsResponse, playersResponse, coachesResponse, lifestylesResponse, artistsResponse, songsResponse] = await Promise.all([
      fetch(`${this.apiBaseUrl}/clubs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
      fetch(`${this.apiBaseUrl}/players`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
      fetch(`${this.apiBaseUrl}/coaches`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
      fetch(`${this.apiBaseUrl}/lifestyles`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
      fetch(`${this.apiBaseUrl}/artists`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
      fetch(`${this.apiBaseUrl}/songs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
    ]);

    // Check all responses
    const responses = [
      { name: 'clubs', response: clubsResponse },
      { name: 'players', response: playersResponse },
      { name: 'coaches', response: coachesResponse },
      { name: 'lifestyles', response: lifestylesResponse },
      { name: 'artists', response: artistsResponse },
      { name: 'songs', response: songsResponse }
    ];

    const failedResponses = responses.filter(r => !r.response.ok);
    if (failedResponses.length > 0) {
      console.error('Failed responses:', failedResponses);
      throw new Error(`Failed to fetch participants data for: ${failedResponses.map(r => r.name).join(', ')}`);
    }

    const [clubsData, playersData, coachesData, lifestylesData, artistsData, songsData] = await Promise.all([
      clubsResponse.json(),
      playersResponse.json(),
      coachesResponse.json(),
      lifestylesResponse.json(),
      artistsResponse.json(),
      songsResponse.json()
    ]);

    return {
      clubs: this.processData(clubsData, 'clubs'),
      players: this.processData(playersData, 'players'),
      coaches: this.processData(coachesData, 'coaches'),
      lifestyles: this.processData(lifestylesData, 'lifestyles'),
      artists: this.processData(artistsData, 'artists'),
      songs: this.processData(songsData, 'songs')
    };
  }

  processData(data, type = null) {
    if (Array.isArray(data)) return data;
    if (data.data) return data.data;
    
    // Check for common response structures
    if (type) {
      if (data[type]) return data[type];
      if (data[type.replace(/s$/, '')]) return data[type.replace(/s$/, '')];
    }
    
    // Check for success property
    if (data.success && data.data) return data.data;
    
    // If it's an object with numeric keys, convert to array
    if (typeof data === 'object' && data !== null) {
      const values = Object.values(data);
      if (values.length > 0 && values.every(v => typeof v === 'object')) {
        return values;
      }
    }
    
    return [];
  }

  formatParticipantForApi(type, item) {
    const baseParticipant = {
      participantType: type,
    };

    // Extract the correct ID based on type
    switch(type) {
      case 'player':
        return {
          ...baseParticipant,
          playerId: item.playerId || item.id
        };
      case 'club':
        return {
          ...baseParticipant,
          clubId: item.clubId || item.id
        };
      case 'coach':
        return {
          ...baseParticipant,
          coachId: item.id || item.coachId
        };
      case 'model':
        return {
          ...baseParticipant,
          modelId: item.id || item.modelId
        };
      case 'artist':
        return {
          ...baseParticipant,
          artistId: item.artist_id || item.id
        };
      case 'song':
        return {
          ...baseParticipant,
          songId: item.song_id || item.id
        };
      default:
        return null;
    }
  }

  async addParticipantsToCompetition(competitionId, participants) {
    // Format participants for the API
    const formattedParticipants = participants.map(p => {
      if (p.participantType && p.participantId) {
        // Already formatted
        return p;
      }
      return this.formatParticipantForApi(p.type, p);
    }).filter(p => p !== null);

    console.log('Sending participants to API:', formattedParticipants);

    const response = await fetch(`${this.apiBaseUrl}/competitions/${competitionId}/participants`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ participants: formattedParticipants }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to add participants");
    }

    return response.json();
  }

  async removeParticipant(competitionId, participantId) {
    const response = await fetch(
      `${this.apiBaseUrl}/competitions/${competitionId}/participants/${participantId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to remove participant');
    }

    return response.json();
  }

  async getCompetitionParticipants(competitionId) {
    const response = await fetch(
      `${this.apiBaseUrl}/competitions/${competitionId}/participants`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch competition participants');
    }

    return response.json();
  }

  async getParticipantsByType(competitionId, type) {
    const response = await fetch(
      `${this.apiBaseUrl}/competitions/${competitionId}/participants/type/${type}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch ${type} participants`);
    }

    return response.json();
  }

  // Helper method to get display name for a participant
  getParticipantDisplayName(participant) {
    if (!participant || !participant.details) return 'Unknown';

    const details = participant.details;
    
    switch(participant.participantType) {
      case 'player':
        return details.name || 'Unknown Player';
      case 'club':
        return details.name || 'Unknown Club';
      case 'coach':
        return details.firstName && details.lastName 
          ? `${details.firstName} ${details.lastName}`
          : details.name || 'Unknown Coach';
      case 'model':
        return details.name || 'Unknown Model';
      case 'artist':
        return details.name || 'Unknown Artist';
      case 'song':
        return details.title || 'Unknown Song';
      default:
        return 'Unknown';
    }
  }

  // Helper method to get image URL for a participant
  getParticipantImage(participant) {
    if (!participant || !participant.details) return null;

    const details = participant.details;
    
    switch(participant.participantType) {
      case 'player':
        return details.profileImageUrl || null;
      case 'club':
        return details.logo || null;
      case 'coach':
        return details.profilePicture || null;
      case 'model':
        return details.profileImageUrl || null;
      case 'artist':
        // Artists might not have an image field yet
        return null;
      case 'song':
        return details.cover_image || details.coverImage || null;
      default:
        return null;
    }
  }

  // Helper method to get additional info for a participant
  getParticipantAdditionalInfo(participant) {
    if (!participant || !participant.details) return null;

    const details = participant.details;
    
    switch(participant.participantType) {
      case 'player':
        return details.position || details.team || null;
      case 'club':
        return details.league || details.country || null;
      case 'coach':
        return details.experience || details.team || null;
      case 'model':
        return details.height ? `${details.height}cm` : null;
      case 'artist':
        return details.country || details.bio?.substring(0, 50) + '...' || null;
      case 'song':
        return details.artist?.name || details.genre || null;
      default:
        return null;
    }
  }
}

export default ParticipantService;